import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { Box, Button, Flex, Heading, Spacer } from '@chakra-ui/react'

import FullTable from '@/components/Table/FullTable/FullTable'
import DateCell from '@/components/Table/components/Cell/DateCell'
import { FilterType } from '@/components/Table/components/Filter/TableFilter'
import { useInitialTableState } from '@/components/Table/components/useInitialTableState'
import useOfferList from '@/features/offers/hooks/useOfferList'
import { authSelector } from '@/stores/auth/authSlice'
import { convertTableStateToQuery } from '@/utils/tableHelper'
import { createColumnHelper } from '@tanstack/react-table'

const OfferGCPRMaterialListPage = () => {
  const { user: currentUser } = useSelector(authSelector)
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()

  const status = searchParams.get('status')

  const initialFilter = useMemo(() => {
    const columnFilters = []

    if (status) {
      columnFilters.push({ id: 'submissionStatus', value: status.split(',') })
    }

    if (columnFilters.length === 0) {
      return {}
    }

    return { columnFilters }
  }, [status])

  const [storedTableState, setStoredTableState] = useInitialTableState(`table_state:offerGCPRMaterial`, {})
  const query = convertTableStateToQuery(storedTableState)

  const { useFetchGCPRMaterials } = useOfferList()
  const { data: offerGCPRMaterialResponse, isRefetching } = useFetchGCPRMaterials(query)

  const columnHelper = createColumnHelper()

  const handleGotoOffer = (row) => {
    if (!row?.original?.offerId) {
      return
    }

    navigate(`/offers/${row.original.offerId}?GCPRSubmissionIndex=${row.original.submissionIndex}`)
  }

  const defaultColumns = [
    columnHelper.accessor('PRNumber', {
      meta: { filterVariant: FilterType.TEXT },
    }),
    columnHelper.accessor('submissionStatus', {
      header: () => 'Status',
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: ['Draft', 'Submitted', 'Approved', 'Rejected', 'Archive'].map((v) => ({
          label: v,
          value: v,
        })),
      },
    }),
    columnHelper.accessor('requestDate', {
      meta: { filterVariant: FilterType.DATE },
      cell: DateCell,
    }),
    columnHelper.accessor('expectedDeliveryDate', {
      header: () => 'Expected Delivery Date',
      meta: { filterVariant: FilterType.DATE },
      cell: DateCell,
    }),
    columnHelper.accessor('modelNumber', {
      meta: { filterVariant: FilterType.TEXT },
    }),
    columnHelper.accessor('description', {
      meta: { filterVariant: FilterType.TEXT },
    }),
    columnHelper.accessor('quantity', {
      meta: { filterVariant: FilterType.RANGE },
    }),
    columnHelper.accessor('purchaseScenario', {
      header: () => 'Purchase Scenario',
      meta: { filterVariant: FilterType.TEXT },
    }),
    columnHelper.accessor('PONumber', {
      header: () => 'PO Number',
      meta: { filterVariant: FilterType.TEXT },
    }),
    columnHelper.accessor('subSystemIndex', {
      header: () => 'Sub System',
      meta: { filterVariant: FilterType.RANGE },
    }),
  ]

  const handleDownload = () => {
    // Placeholder: add export behavior if needed later.
  }

  useEffect(() => {
    if (Object.keys(initialFilter).length > 0) {
      setStoredTableState((prev) => ({
        ...prev,
        ...initialFilter,
      }))

      navigate(location.pathname, { replace: true })
    }
  }, [initialFilter, navigate, location.pathname, setStoredTableState])

  if (!currentUser?.fullName) {
    return <>Loading...</>
  }

  return (
    <Box shadow="md" borderWidth="1px" bg="white" w="100%" mb="auto">
      <Flex p={5} borderBottom="1px solid #d8dbe0">
        <Heading>GCPR Materials ({offerGCPRMaterialResponse?.data.count ?? 0})</Heading>
        <Spacer />

        <Flex gap={1} alignItems={'center'}>
          <Button colorScheme={'green'} onClick={handleDownload}>
            Download
          </Button>
        </Flex>
      </Flex>
      <Box p={5} overflow={'scroll'}>
        <FullTable
          data={offerGCPRMaterialResponse?.data.data ? offerGCPRMaterialResponse.data.data : []}
          rowCount={offerGCPRMaterialResponse?.data.count}
          columnDef={defaultColumns}
          state={storedTableState}
          setState={setStoredTableState}
          handleRowClick={handleGotoOffer}
          isLoading={isRefetching}
        />
      </Box>
    </Box>
  )
}

export default OfferGCPRMaterialListPage
